import { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Row,
  Col,
  Divider,
  Alert,
} from "antd";
import { AppstoreOutlined, FolderOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import axiosInstance from "../../../variables/api/axiosInstance";

const { TextArea } = Input;
const { Option } = Select;

const AddCategorie = ({
  show,
  handleClose,
  editMode = false,
  selectedCategorie,
  existingCategories,
  branches = [],
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      if (editMode && selectedCategorie) {
        form.setFieldsValue({
          nom: selectedCategorie.nom || "",
          codeCategorie: selectedCategorie.codeCategorie || "",
          abbreviation: selectedCategorie.abbreviation || "",
          commentaire: selectedCategorie.commentaire || "",
          valeurVenaleMinimale: selectedCategorie.valeurVenaleMinimale || "",
          nombrePlaceParDefaut: selectedCategorie.nombrePlaceParDefaut ?? "",
          nombrePlaceMin: selectedCategorie.nombrePlaceMin ?? "",
          nombrePlaceMax: selectedCategorie.nombrePlaceMax ?? "",
          brancheId: selectedCategorie.branche?.brancheId ?? null,
        });
      } else {
        form.resetFields();
      }
    }
  }, [show, editMode, selectedCategorie, form]);

  const onSubmit = async (values) => {
    // Vérification des doublons
    const isDuplicateNom = existingCategories.some(
      (cat) =>
        cat.nom.toLowerCase() === values.nom.toLowerCase() &&
        (editMode ? cat.categorieId !== selectedCategorie.categorieId : true)
    );

    const isDuplicateCode = existingCategories.some(
      (cat) =>
        cat.codeCategorie.toLowerCase() === values.codeCategorie.toLowerCase() &&
        (editMode ? cat.categorieId !== selectedCategorie.categorieId : true)
    );

    if (isDuplicateNom) {
      toast.error("Une catégorie avec ce nom existe déjà.");
      return;
    }

    if (isDuplicateCode) {
      toast.error("Une catégorie avec ce code existe déjà.");
      return;
    }

    // Validation des places
    const { nombrePlaceMin, nombrePlaceMax, nombrePlaceParDefaut } = values;
    if (nombrePlaceMin && nombrePlaceMax && nombrePlaceMin > nombrePlaceMax) {
      toast.error("Le minimum ne peut pas dépasser le maximum.");
      return;
    }
    if (
      nombrePlaceParDefaut &&
      nombrePlaceMin &&
      nombrePlaceParDefaut < nombrePlaceMin
    ) {
      toast.error("La valeur par défaut doit être ≥ au minimum.");
      return;
    }
    if (
      nombrePlaceParDefaut &&
      nombrePlaceMax &&
      nombrePlaceParDefaut > nombrePlaceMax
    ) {
      toast.error("La valeur par défaut doit être ≤ au maximum.");
      return;
    }

    setLoading(true);

    const categorieData = {
      ...(editMode && { categorieId: selectedCategorie.categorieId }),
      nom: values.nom,
      codeCategorie: values.codeCategorie,
      abbreviation: values.abbreviation,
      commentaire: values.commentaire,
      valeurVenaleMinimale: values.valeurVenaleMinimale,
      nombrePlaceParDefaut: values.nombrePlaceParDefaut || null,
      nombrePlaceMin: values.nombrePlaceMin || null,
      nombrePlaceMax: values.nombrePlaceMax || null,
      branche: values.brancheId
        ? { brancheId: values.brancheId }
        : null,
    };

    try {
      let response;
      if (editMode) {
        response = await axiosInstance.put(`/api/categorie`, categorieData);
        toast.success("Catégorie modifiée avec succès");
      } else {
        response = await axiosInstance.post(`/api/categorie`, categorieData);
        toast.success("Catégorie ajoutée avec succès");
      }

      const affectedCategoryId = editMode
        ? selectedCategorie.categorieId
        : response.data.categorieId;

      form.resetFields();
      handleClose(affectedCategoryId);
    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 409) {
        toast.error("Cette catégorie existe déjà sur le serveur.");
      } else {
        toast.error(
          editMode
            ? "Erreur lors de la modification"
            : "Erreur lors de l'ajout"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={show}
      onCancel={() => {
        form.resetFields();
        handleClose();
      }}
      footer={null}
      width={800}
      title={
        <Space>
          <AppstoreOutlined style={{ color: "#667eea" }} />
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            {editMode ? "Modifier la catégorie" : "Nouvelle catégorie"}
          </span>
        </Space>
      }
    >
      <div style={{ padding: "8px 0" }}>
        <Alert
          message="Information"
          description={
            editMode
              ? "Modifiez les informations de la catégorie"
              : "Ajoutez une nouvelle catégorie d'assurance"
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form form={form} layout="vertical" onFinish={onSubmit}>
          {/* Section: Informations principales */}
          <Divider orientation="left">Informations principales</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Branche"
                name="brancheId"
                rules={[{ required: true, message: "La branche est requise" }]}
              >
                <Select
                  placeholder="Sélectionner une branche"
                  size="large"
                  showSearch
                  optionFilterProp="children"
                  suffixIcon={<FolderOutlined />}
                >
                  {branches.map((b) => (
                    <Option key={b.brancheId} value={b.brancheId}>
                      {b.nom} ({b.codeBranche})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Nom de la catégorie"
                name="nom"
                rules={[
                  { required: true, message: "Le nom est requis" },
                  {
                    min: 3,
                    message: "Le nom doit contenir au moins 3 caractères",
                  },
                ]}
              >
                <Input placeholder="Ex: Véhicule particulier" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Code"
                name="codeCategorie"
                rules={[{ required: true, message: "Le code est requis" }]}
              >
                <Input
                  placeholder="Ex: VP"
                  size="large"
                  style={{ textTransform: "uppercase" }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Abréviation"
                name="abbreviation"
                rules={[
                  { required: true, message: "L'abréviation est requise" },
                ]}
              >
                <Input
                  placeholder="Ex: VEH"
                  size="large"
                  style={{ textTransform: "uppercase" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="commentaire">
            <TextArea
              rows={3}
              placeholder="Description optionnelle de la catégorie..."
            />
          </Form.Item>

          {/* Section: Valeur vénale */}
          <Divider orientation="left">Valeur vénale</Divider>

          <Form.Item
            label="Valeur vénale minimale (FCFA)"
            name="valeurVenaleMinimale"
            rules={[
              {
                required: true,
                message: "La valeur vénale minimale est requise",
              },
              { type: "number", min: 0, message: "La valeur doit être ≥ 0" },
            ]}
          >
            <InputNumber
              placeholder="Ex: 1000000"
              size="large"
              style={{ width: "100%" }}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
              }
              parser={(value) => value.replace(/\s?/g, "")}
            />
          </Form.Item>

          {/* Section: Nombre de places */}
          <Divider orientation="left">Configuration des places (optionnel)</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Nombre par défaut"
                name="nombrePlaceParDefaut"
                rules={[
                  { type: "number", min: 1, message: "Doit être ≥ 1" },
                ]}
              >
                <InputNumber
                  placeholder="Ex: 5"
                  size="large"
                  style={{ width: "100%" }}
                  min={1}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Minimum"
                name="nombrePlaceMin"
                rules={[
                  { type: "number", min: 1, message: "Doit être ≥ 1" },
                ]}
              >
                <InputNumber
                  placeholder="Ex: 2"
                  size="large"
                  style={{ width: "100%" }}
                  min={1}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Maximum"
                name="nombrePlaceMax"
                rules={[
                  { type: "number", min: 1, message: "Doit être ≥ 1" },
                ]}
              >
                <InputNumber
                  placeholder="Ex: 9"
                  size="large"
                  style={{ width: "100%" }}
                  min={1}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Boutons d'action */}
          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  form.resetFields();
                  handleClose();
                }}
                disabled={loading}
                size="large"
              >
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                style={{
                  background: "#667eea",
                  borderColor: "#667eea",
                }}
              >
                {editMode ? "Modifier" : "Créer"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default AddCategorie;